import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { updateCustomer, clearUpdateError, fetchInvoiceEditData } from '../../../features/customer/customer.slice';
import type { CustomerFormData, ValidationErrors, AddCustomerModalProps } from '../types';
import {
    validateCurrentStep,
    validateVatNumber as validateVatNumberUtil
} from '../validations';
import BusinessInformation from './BusinessInformation';
import ContactInformation from './ContactInformation';
import Preferences from './Preferences';
import StepIndicator from './StepIndicator';
import '../styles/add-customer-modal.scss';
import { handleGLNInput, handleNumberOnlyInput, handlePhoneNumberInput, handleSSNInput, handleSSNKeyDown, handleVatNumberInput, handleVatNumberKeyDown, handleZipCodeInput } from '../helpers/input-restriction.helper';
import { Customer } from '../../../features/customer/customer.service';
import { useSelector } from 'react-redux';
import { InvoiceTypes } from '../../../../common/constants';
import { validateVatNumberApi } from '../../../api/customer';
import { europeanVatCountryCodes } from '../../../../common/StringUtils';

interface EditCustomerModalProps extends Omit<AddCustomerModalProps, 'isEdit' | 'initialData'> {
    selectedCustomer?: number | string | undefined;
}

const EditCustomerModal: React.FC<EditCustomerModalProps> = ({
    show,
    onHide,
    onSave,
    selectedCustomer,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { updateLoading, updateError } = useSelector((state: any) => state?.customer || {});
    const customerState = useSelector((state: any) => state?.customer || {});
    const userID = useSelector((state: any) => state?.auth?.user?.userId);

    //   useEffect(() => {
    //     if (userID) {
    //       dispatch(fetchInvoiceEditData(userID));
    //     }
    //   }, [dispatch, userID]);

    const mapAccountTypeToBusinessType = (accountType: string | undefined): string => {
        if (!accountType) return '';
        
        // Map API accountType values to form businessType values
        const accountTypeStr = accountType.toString();
        if (accountTypeStr === '1' || accountTypeStr === 'PRIVATE_CUSTOMER') {
            return '1';
        } else if (accountTypeStr === '2' || accountTypeStr === 'BUSINESS_CUSTOMER') {
            return '2';
        } else if (accountTypeStr === '3' || accountTypeStr === 'BUSINESS_CONSTRUCTION') {
            return '3';
        }
        return accountTypeStr;
    };

    const getCurrentCustomerData = () => {
        if (!selectedCustomer) return;
    
        const customersList = customerState.customers || [];
        const fullCustomerData = customersList.find((c: Customer) => {
            if (!c) return false;
            const customerIdToCheck = c.customerId || c.id;
            return customerIdToCheck?.toString() === selectedCustomer?.toString();
        });
    
        if (fullCustomerData) {
            const mappedBusinessType = mapAccountTypeToBusinessType(fullCustomerData.accountType);

            // Warn user if customerAddress data is missing (invoiceType and careOf will use defaults)
            if (!fullCustomerData.customerAddress?.[0]) {
                toast.error(String(t('CUSTOMER_ADDRESS_DATA_MISSING') || 'Some customer address details could not be loaded. Defaults will be used.'));
            }

            return {
                customerId: fullCustomerData.customerId || undefined,
                accountType: fullCustomerData.accountType || '',
                businessType: mappedBusinessType,
                customerName: fullCustomerData.name || '',
                orgNo: fullCustomerData.orgNo || '',
                customerAddress: fullCustomerData.address || '',
                ssn: fullCustomerData.ssn || '',
                country: fullCustomerData.country || 'Sweden', 
                city: fullCustomerData.city || '',
                zipCode: fullCustomerData.zipCode || '',
                addressLine1: fullCustomerData.address || '',
                addressLine2: fullCustomerData.address2 || '',
                hasVATNumber: fullCustomerData.vatNumber ? 'true' : 'false',
                vatNumber: fullCustomerData.vatNumber || '',
                vatCountryCode: (() => {
                    if (fullCustomerData.vatCountryCode) return fullCustomerData.vatCountryCode;
                    // Extract country code from vatNumber prefix (e.g., "US123..." → "US")
                    const vat = fullCustomerData.vatNumber || '';
                    if (vat.length >= 2) {
                        const prefix = vat.substring(0, 2).toUpperCase();
                        if (/^[A-Z]{2}$/.test(prefix)) return prefix;
                    }
                    return 'SE';
                })(),
                customerReference: fullCustomerData.customerReference || '',
                contactEmail: fullCustomerData.email || '',
                ccEmail: fullCustomerData.ccEmail || '',
                contactPhone: fullCustomerData.phoneNumber || '',
                phoneNumber: fullCustomerData.phoneNumber || '',
                invoiceType: fullCustomerData.customerAddress?.[0]?.invoiceType ? String(fullCustomerData.customerAddress[0].invoiceType) : '1',
                ourElectronicReference: fullCustomerData.ourElectronicReference || '7230020',
                yourElectronicReference: fullCustomerData.yourElectronicReference || '',
                globalLocationNumber: fullCustomerData.globalLocationNumber || '',
                extraField1: fullCustomerData.extraField1 || '',
                extraField2: fullCustomerData.extraField2 || '',
                careOf: fullCustomerData.customerAddress?.[0]?.co || '',
                // Email delivery checkbox and secondary email
                useExistingEmail: !fullCustomerData.secondaryEmailAddress, // If no secondary email, use existing
                secondaryEmail: fullCustomerData.secondaryEmailAddress || '',
                // Postal delivery checkbox and address fields
                useExistingAddress: !fullCustomerData.postAddress, // If no postal address, use existing
                postAddress: fullCustomerData.postAddress || '',
                postCity: fullCustomerData.postCity || '',
                postCountryCode: fullCustomerData.postCountryCode || '',
                postZipCode: fullCustomerData.postZipCode || '',
                postCountry: fullCustomerData.postCountry || '',
                paymentTerms: fullCustomerData.paymentTerm || fullCustomerData.paymentTerms || '30',
                invoiceCurrency: fullCustomerData.invoiceCurrency || 'SEK',
                invoiceLanguage: (() => {
                    // Convert API language format (sv-se/en-us) to dropdown format (sv-SE/en-US)
                    // API returns 'language' field, not 'invoiceLanguage'
                    const apiLang = (fullCustomerData as any).language || fullCustomerData.invoiceLanguage || '';
                    console.log('🔍 [GET CUSTOMER DATA] Raw language from API:', apiLang);
                    const lowerLang = apiLang.toLowerCase();
                    let result = 'sv-SE'; // default to Swedish
                    if (lowerLang === 'sv-se' || lowerLang === 'swedish') result = 'sv-SE';
                    else if (lowerLang === 'en-us' || lowerLang === 'english') result = 'en-US';
                    else if (apiLang) result = apiLang;
                    console.log('🔍 [GET CUSTOMER DATA] Converted to dropdown format:', result);
                    return result;
                })(),
                vatDetails: fullCustomerData.vatDetails || '',
                reference: fullCustomerData.reference || '',
                isVATRegistered: (() => {
                    // API uses 'isVATRegistred' (with 'd'), form uses 'isVATRegistered' (with 'ed')
                    const rawValue = (fullCustomerData as any).isVATRegistred || fullCustomerData.isVATRegistered;
                    const convertedValue = rawValue === true || rawValue === 'true' ? 'true' : 'false';
                    return convertedValue;
                })(),
                isVatVerified: (fullCustomerData as any).isVatVerified ?? false,
            };
        }
    
        return;
    };

    const [currentStep, setCurrentStep] = useState<number>(1);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [vatNumberError, setVatNumberError] = useState<string>('');
    const [isVatValidating, setIsVatValidating] = useState<boolean>(false);
    const [vatApiValidationStatus, setVatApiValidationStatus] = useState<'valid' | 'invalid' | 'error' | null>(null);
    const lastValidatedVatRef = React.useRef<string>('');
    const originalVatRef = React.useRef<{ vatNumber: string; vatCountryCode: string; isVatVerified: boolean }>({
        vatNumber: '',
        vatCountryCode: '',
        isVatVerified: false,
    });
    const [refreshLoading, setRefreshLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<CustomerFormData>({
        customerId: '',
        businessType: '',
        accountType: '',
        customerName: '',
        orgNo: '',
        ssn: '',
        customerAddress: '',
        country: 'Sweden',
        city: '',
        zipCode: '',
        addressLine1: '',
        addressLine2: '',
        hasVATNumber: 'true',
        vatNumber: '',
        vatCountryCode: 'SE',
        customerReference: '',
        contactEmail: '',
        ccEmail: '',
        contactPhone: '',
        invoiceDeliveryMethod: '1',
        phoneNumber: '',
        ourElectronicReference: '7230020',
        yourElectronicReference: '',
        globalLocationNumber: '',
        extraField1: '',
        extraField2: '',
        careOf: '',
        // Email delivery checkbox and secondary email
        useExistingEmail: true,
        secondaryEmail: '',
        // Postal delivery checkbox and address fields
        useExistingAddress: true,
        postAddress: '',
        postCity: '',
        postCountryCode: '',
        postZipCode: '',
        postCountry: '',
        paymentTerms: '30',
        invoiceCurrency: 'SEK',
        invoiceLanguage: 'Swedish',
        vatDetails: '',
        reference: '',
        isVATRegistered: 'false',
    });

    useEffect(() => {
        if (show) {
            // Always try to get the full customer data from Redux store
            const fullCustomerData = getCurrentCustomerData();
            if (fullCustomerData) {
                originalVatRef.current = {
                    vatNumber: fullCustomerData.vatNumber ?? '',
                    vatCountryCode: fullCustomerData.vatCountryCode ?? '',
                    isVatVerified: fullCustomerData.isVatVerified ?? false,
                };
                if (fullCustomerData.isVatVerified) {
                    setVatApiValidationStatus('valid');
                }
                setFormData({
                    ...fullCustomerData,
                    invoiceDeliveryMethod: fullCustomerData.invoiceType || '1', // Ensure this field is present
                });
            } else {
                // Reset to empty state if no customer data available
                setFormData({
                    customerId: '',
                    businessType: '',
                    accountType: '',
                    customerName: '',
                    orgNo: '',
                    ssn: '',
                    customerAddress: '',
                    country: 'Sweden',
                    city: '',
                    zipCode: '',
                    addressLine1: '',
                    addressLine2: '',
                    hasVATNumber: 'true',
                    vatNumber: '',
                    vatCountryCode: 'SE',
                    customerReference: '',
                    contactEmail: '',
                    ccEmail: '',
                    contactPhone: '',
                    phoneNumber: '',
                    invoiceDeliveryMethod: '1',
                    ourElectronicReference: '7230020',
                    yourElectronicReference: '',
                    globalLocationNumber: '',
                    extraField1: '',
                    extraField2: '',
                    careOf: '',
                    // Email delivery checkbox and secondary email
                    useExistingEmail: true,
                    secondaryEmail: '',
                    // Postal delivery checkbox and address fields
                    useExistingAddress: true,
                    postAddress: '',
                    postCity: '',
                    postCountryCode: '',
                    postZipCode: '',
                    postCountry: '',
                    paymentTerms: '30',
                    invoiceCurrency: 'SEK',
                    invoiceLanguage: 'Swedish',
                    vatDetails: '',
                    reference: '',
                    isVATRegistered: 'false',
                });
            }
            setErrors({});
            setVatNumberError('');
        }
    }, [show, selectedCustomer, customerState]);


  
    

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => {
            // Handle boolean checkbox fields
            let processedValue: any = value;
            if (field === 'useExistingEmail' || field === 'useExistingAddress') {
                processedValue = value === 'true';
            }
            
            const newFormData = {
                ...prev,
                [field]: processedValue
            };
            // Clear secondaryEmail when useExistingEmail is checked
            if (field === 'useExistingEmail' && processedValue === true) {
                newFormData.secondaryEmail = '';
            }

            // Clear postal address fields when useExistingAddress is checked
            if (field === 'useExistingAddress' && processedValue === true) {
                newFormData.postAddress = '';
                newFormData.postCity = '';
                newFormData.postZipCode = '';
                newFormData.postCountry = '';
                newFormData.postCountryCode = '';
            }
            
            return newFormData;
        });

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }

        if (field === 'vatCountryCode' || field === 'vatNumber') {
            setVatNumberError('');
            setVatApiValidationStatus(null);
            lastValidatedVatRef.current = '';
        }

        if (field === 'country') {
            const countryToVatCode: { [key: string]: string } = {
                'Sweden': 'SE',
                'USA': 'US',
                'Canada': 'CA',
                'Panama': 'PA',
                'Norway': 'NO',
                'Denmark': 'DK',
                'Finland': 'FI',
                'Germany': 'DE',
                'France': 'FR',
                'United Kingdom': 'GB'
            };

            const vatCode = countryToVatCode[value];
            if (vatCode) {
                setFormData(prev => ({
                    ...prev,
                    [field]: value,
                    vatCountryCode: vatCode
                }));
            }
        }
        
        // Auto-update postal country code when postal country changes
        if (field === 'postCountry') {
            const countryToCode: { [key: string]: string } = {
                'Sweden': 'SE',
                'USA': 'US',
                'Canada': 'CA',
                'Panama': 'PA',
                'Norway': 'NO',
                'Denmark': 'DK',
                'Finland': 'FI',
                'Germany': 'DE',
                'France': 'FR',
                'United Kingdom': 'GB'
            };

            const countryCode = countryToCode[value];
            if (countryCode) {
                setFormData(prev => ({
                    ...prev,
                    [field]: value,
                    postCountryCode: countryCode
                }));
            }
        }
    };

    const validateVatNumberWithError = (vatCountryCode: string, vatNumber: string): boolean => {
        return validateVatNumberUtil(vatCountryCode, vatNumber, setVatNumberError, t);
    };

    const validateCurrentStepForm = (): boolean => {
        const { errors: newErrors, isValid } = validateCurrentStep(
            currentStep,
            formData,
            validateVatNumberWithError
        );

        setErrors(newErrors);
        return isValid;
    };

    const transformFormDataToAPI = useCallback((data: CustomerFormData) => {
        let concatVatNumber = '';
        if (data.isVATRegistered !== 'false' && data.hasVATNumber === 'true' && data.vatNumber) {
            // Check if vatNumber already starts with country code to prevent duplication (e.g., RSRS)
            if (data.vatNumber.toUpperCase().startsWith(data.vatCountryCode.toUpperCase())) {
                // Already has country code, use as-is
                concatVatNumber = data.vatNumber;
                } else {
                // Add country code
                concatVatNumber = data.vatCountryCode + data.vatNumber;
               }
        }

        const apiData = {
            name: data.customerName,
            businessType: data.businessType,
            accountType: data.businessType,
            orgNo: data.orgNo,
            ssn: data.ssn,
            country: data.country,
            city: data.city,
            zipCode: data.zipCode,
            // include both legacy keys and explicit addressLine1/2 to match API types
            address: data.addressLine1,
            address2: data.addressLine2,
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2,
            hasVATNumber: data.hasVATNumber,
            ...(data.isVATRegistered !== 'false' && {
                vatNumber: concatVatNumber || data.vatNumber,
                vatCountryCode: data.vatCountryCode,
            }),
            yourElectronicReference: data.ourElectronicReference,
            globalLocationNumber: data.globalLocationNumber,
            extraField1: data.extraField1,
            extraField2: data.extraField2,
            co: data.careOf,
            invoiceCurrency: data.invoiceCurrency,
            invoiceLanguage: data.invoiceLanguage, // Pass invoiceLanguage to API service
            vatDetails: data.vatDetails,
            email: data.contactEmail || '',
            ccEmail: data.ccEmail || '',
            countryCode: data.isVATRegistered !== 'false' ? (data.vatCountryCode || 'SE') : 'SE',
            paymentTerms: data.paymentTerms || '30',
            id: data.customerId,
            invoiceType: data.invoiceDeliveryMethod || '1',
            reference: data?.reference,
            phoneNumber: data?.phoneNumber || '',
            // API expects 'isVATRegistred' (with 'd'), convert string to boolean
            isVATRegistred: data?.isVATRegistered === 'true',
            isVatVerified: data?.isVatVerified ?? false,
            // Secondary email - only send if useExistingEmail is false (user unchecked the box)
            ...(data.invoiceDeliveryMethod === '1' && !data.useExistingEmail && data.secondaryEmail && {
                secondaryEmailAddress: data.secondaryEmail,
                isSecondaryEmailAddress: true,
            }),
            // Set isSecondaryEmailAddress to false when using existing email
            ...(data.invoiceDeliveryMethod === '1' && data.useExistingEmail && {
                isSecondaryEmailAddress: false,
            }),
            // Postal delivery address fields - only send if useExistingAddress is false
            ...(data.invoiceDeliveryMethod === '3' && !data.useExistingAddress && {
                postAddress: data.postAddress || '',
                postCity: data.postCity || '',
                postCountryCode: data.postCountryCode || '',
                postZipCode: data.postZipCode || '',
                postCountry: data.postCountry || '',
            }),
        };

        return apiData;
    }, []);

    const handleNext = async () => {
        if (!validateCurrentStepForm()) {
            return;
        }

        // On step 1: validate VAT on Next click (only for European VAT country codes)
        // Skip if the VAT was already verified and the number hasn't changed
        const isVatAlreadyVerified =
            originalVatRef.current.isVatVerified &&
            (formData.vatNumber ?? '') === originalVatRef.current.vatNumber &&
            (formData.vatCountryCode ?? '') === originalVatRef.current.vatCountryCode;

        // A customer cannot be updated (or moved past this step) with a VAT
        // number that fails verification - block progression and surface a
        // clear validation message instead of just warning.
        if (
            currentStep === 1 &&
            formData.isVATRegistered === 'true' &&
            formData.vatNumber?.trim() &&
            europeanVatCountryCodes.has(formData.vatCountryCode) &&
            !isVatAlreadyVerified
        ) {
            const status = await handleVatBlur(formData.vatNumber.trim());
            if (status === 'invalid') {
                setVatNumberError('VAT_VERIFICATION_REQUIRED_TO_CONTINUE');
                return;
            }
        }

        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
        } else {
            try {
                const customerData = {
                    ...transformFormDataToAPI(formData),
                    id: formData.customerId as number
                };

                const result = await dispatch(updateCustomer(customerData));

                if (updateCustomer.fulfilled.match(result)) {
                   
                    // Refresh customer data after successful update
                    if (userID) {
                        try {
                            setRefreshLoading(true);
                            await dispatch(fetchInvoiceEditData(userID));
                        } finally {
                            setRefreshLoading(false);
                        }
                    }
                    
                    // Pass the updated customer ID to parent
                    onSave({ ...formData, customerId: formData.customerId });
                    onHide();
                    resetForm();
                } else if (updateCustomer.rejected.match(result)) {
                    // Error toast is already shown in the thunk, no need to show another one
                    console.error('Failed to update customer:', result.payload || result.error);
                }
            } catch (error) {
                console.error('Error updating customer:', error);
                toast.error(t('FAILED_TO_SAVE_CUSTOMER') as string);
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const resetForm = () => {
        setCurrentStep(1);
        setErrors({});
        setVatNumberError('');
    };

    const handleCancel = () => {
        dispatch(clearUpdateError());
        setCurrentStep(1);
        setErrors({});
        setVatNumberError('');
        setIsVatValidating(false);
        setVatApiValidationStatus(null);
        lastValidatedVatRef.current = '';
        originalVatRef.current = { vatNumber: '', vatCountryCode: '', isVatVerified: false };
        onHide();
    };

    // Input restriction handlers

    const handleVatBlur = async (vatNumber: string): Promise<'valid' | 'invalid' | 'error' | null> => {
        const fullVatNumber = vatNumber?.trim();
        if (!fullVatNumber) return null;
        // Already validated the same number — return current status without re-calling API
        if (fullVatNumber === lastValidatedVatRef.current) return vatApiValidationStatus ?? null;
        lastValidatedVatRef.current = fullVatNumber;

        setIsVatValidating(true);
        setVatApiValidationStatus(null);
        try {
            const result = await validateVatNumberApi(fullVatNumber);
            const isValid = result.response.isValid;
            const status: 'valid' | 'invalid' = isValid ? 'valid' : 'invalid';
            setVatApiValidationStatus(status);
            setFormData(prev => ({ ...prev, isVatVerified: isValid }));
            return status;
        } catch {
            setVatApiValidationStatus('error');
            return 'error';
        } finally {
            setIsVatValidating(false);
        }
    };

    const isVatAlreadyVerified =
        originalVatRef.current.isVatVerified &&
        (formData.vatNumber ?? '') === originalVatRef.current.vatNumber &&
        (formData.vatCountryCode ?? '') === originalVatRef.current.vatCountryCode;

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:

                return (
                    <BusinessInformation
                        formData={formData}
                        errors={errors}
                        vatNumberError={vatNumberError}
                        setVatNumberError={setVatNumberError}
                        onInputChange={handleInputChange}
                        onNumberOnlyInput={handleNumberOnlyInput}
                        onSSNInput={handleSSNInput}
                        onSSNKeyDown={handleSSNKeyDown}
                        onVatNumberInput={(e) => handleVatNumberInput(e, formData)}
                        onVatNumberKeyDown={(e) => handleVatNumberKeyDown(e, formData)}
                        onZipCodeInput={handleZipCodeInput}
                        isVatValidating={isVatValidating}
                        vatApiValidationStatus={vatApiValidationStatus}
                        onVatBlur={(vatNumber) => handleVatBlur(vatNumber)}
                        isVatAlreadyVerified={isVatAlreadyVerified}
                    />
                );
            case 2:
                return (
                    <ContactInformation
                        formData={formData}
                        errors={errors}
                        onInputChange={handleInputChange}
                        onPhoneNumberInput={handlePhoneNumberInput}
                        onGLNInput={handleGLNInput}
                    />
                );
            case 3:
                return (
                    <Preferences
                        formData={formData}
                        errors={errors}
                        onInputChange={handleInputChange}
                    />
                );
            default:
                return (
                    <BusinessInformation
                        formData={formData}
                        errors={errors}
                        vatNumberError={vatNumberError}
                        setVatNumberError={setVatNumberError}
                        onInputChange={handleInputChange}
                        onNumberOnlyInput={handleNumberOnlyInput}
                        onSSNInput={handleSSNInput}
                        onSSNKeyDown={handleSSNKeyDown}
                        onVatNumberInput={(e) => handleVatNumberInput(e, formData)}
                        onVatNumberKeyDown={(e) => handleVatNumberKeyDown(e, formData)}
                        onZipCodeInput={handleZipCodeInput}
                        isVatValidating={isVatValidating}
                        vatApiValidationStatus={vatApiValidationStatus}
                        onVatBlur={(vatNumber) => handleVatBlur(vatNumber)}
                        isVatAlreadyVerified={isVatAlreadyVerified}
                    />
                );
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleCancel}
            size="xl"
            className="add-customer-modal"
            centered
        >
            <Modal.Header className="custom-modal-header">
                <Modal.Title>{t('EDIT_CUSTOMER_MODAL_TITLE')}</Modal.Title>
                <Button
                    variant="link"
                    className="close-btn"
                    onClick={handleCancel}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </Button>
            </Modal.Header>

            <Modal.Body className="custom-modal-body">
                <StepIndicator currentStep={currentStep} />
                <div className="step-divider-full"></div>

                {updateError && (
                    <div className="alert alert-danger mb-3" role="alert">
                        {updateError}
                    </div>
                )}

                {renderStepContent()}
            </Modal.Body>

            <Modal.Footer className="custom-modal-footer">
                {currentStep === 1 ? (
                    <>
                        <Button
                            variant="outline-primary"
                            className="cancel-btn"
                            onClick={handleCancel}
                        >
                            {t('CANCEL')}
                        </Button>
                        <Button
                            variant="primary"
                            className="next-btn"
                            onClick={handleNext}
                        >
                            {t('NEXT')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            variant="outline-primary"
                            className="cancel-btn back-btn"
                            onClick={handleBack}
                        >
                            {t('BACK')}
                        </Button>
                        <div className="d-flex gap-3">
                            <Button
                                variant="outline-secondary"
                                className="cancel-btn"
                                onClick={handleCancel}
                            >
                                {t('CANCEL')}
                            </Button>
                            <Button
                                variant="primary"
                                className="next-btn"
                                onClick={handleNext}
                                disabled={updateLoading || refreshLoading}
                            >
                                {currentStep === 3 ? (
                                    <>
                                        {updateLoading || refreshLoading ? (
                                            <>
                                                {t('UPDATING')}
                                                <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                                            </>
                                        ) : (
                                            <>
                                                {t('UPDATE_DETAILS')}
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: '8px' }}>
                                                    <path d="M3.33203 8H11.9987M8.66536 4L12.194 7.5286C12.4543 7.78894 12.4543 8.21106 12.194 8.4714L8.66536 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                                </svg>
                                            </>
                                        )}
                                    </>
                                ) : t('NEXT')}
                            </Button>
                        </div>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default EditCustomerModal;
